/**
 * Identity helpers: device_id (stable across sessions), session_id (rotates
 * after 30min inactivity), and event_id (per-event uuid for dedup).
 *
 * All state in localStorage so SSR is safe (functions guard `window`).
 */

const DEVICE_KEY = 'trace_device_id';
const SESSION_KEY = 'trace_session_id';
const SESSION_LAST_TS_KEY = 'trace_session_last_ts';
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // RFC4122-ish fallback
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getDeviceId(): string {
  if (typeof window === 'undefined') return 'ssr';
  let id = window.localStorage.getItem(DEVICE_KEY);
  if (!id) {
    id = uuid();
    try {
      window.localStorage.setItem(DEVICE_KEY, id);
    } catch {
      /* private mode etc. — fall through */
    }
  }
  return id;
}

export function getSessionId(): string {
  if (typeof window === 'undefined') return 'ssr';
  const now = Date.now();
  const lastTs = Number(window.localStorage.getItem(SESSION_LAST_TS_KEY) || 0);
  let id = window.localStorage.getItem(SESSION_KEY);
  if (!id || !lastTs || now - lastTs > SESSION_TIMEOUT_MS) {
    id = uuid();
    try {
      window.localStorage.setItem(SESSION_KEY, id);
    } catch {
      /* swallow */
    }
  }
  try {
    window.localStorage.setItem(SESSION_LAST_TS_KEY, String(now));
  } catch {
    /* swallow */
  }
  return id;
}

export function newEventId(): string {
  return uuid();
}

/**
 * Persisted user_id — set on identify(), cleared on logout. Falls back to
 * device_id for anonymous users so events are still attributable.
 */
const USER_ID_KEY = 'trace_user_id';

export function getUserId(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  return window.localStorage.getItem(USER_ID_KEY) || undefined;
}

export function setUserId(userId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (userId) window.localStorage.setItem(USER_ID_KEY, userId);
    else window.localStorage.removeItem(USER_ID_KEY);
  } catch {
    /* swallow */
  }
}
